import { useEffect, useRef, useState } from "react";
import { ImagePlus, Trash2, ArrowUp } from "lucide-react";
import { toast } from "sonner";

import { ChalkActionButton, ChalkCard, SectionTitle } from "../chalk/primitives";
import { assetUrl, tournaments } from "../../lib/tournamentApi";
import { inputStyle } from "./shared";

const ACCEPT = "image/png,image/jpeg,image/webp";

export default function BrandingPanel({ t }) {
  const [b, setB] = useState(null);
  const load = () => tournaments.branding(t.id).then(setB).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, [t.id]); // eslint-disable-line react-hooks/exhaustive-deps
  async function run(fn, ok) {
    try { await fn(); if (ok) toast.success(ok); await load(); }
    catch (e) { toast.error(e.message || "Couldn't save."); }
  }
  if (!b) return null;
  return (
    <div data-testid="branding-panel">
      <SectionTitle label="Director logo" />
      <ChalkCard style={{ marginBottom: 12, display: "flex", gap: 12, alignItems: "center" }}>
        <LogoBox url={assetUrl(b.logo_url)} testId="director-logo-preview" />
        <div style={{ flex: 1, fontSize: 12, color: "var(--chalk-muted)" }}>PNG, JPG or WebP · up to 2MB. Shows on the Hub, Big Screen, results graphics and posters.</div>
        <FilePick testId="director-logo-upload" label={b.logo_url ? "Replace" : "Upload"}
                  onFile={(f) => run(() => tournaments.uploadLogo(t.id, f), "Logo saved.")} />
        {b.logo_url && <IconBtn testId="director-logo-remove" label="Remove logo" onClick={() => run(() => tournaments.removeLogo(t.id), "Logo removed.")}><Trash2 size={15} /></IconBtn>}
      </ChalkCard>
      <SectionTitle label={`Sponsors (${b.sponsors.length}/8)`} />
      {b.sponsors.map((s, i) => (
        <ChalkCard key={s.id} data-testid={`sponsor-row-${s.id}`} style={{ marginBottom: 6, display: "flex", gap: 10, alignItems: "center" }}>
          <LogoBox url={assetUrl(s.logo_url)} small />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{s.name}</div>
            {s.url && <div style={{ fontSize: 11, color: "var(--chalk-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.url}</div>}
          </div>
          {i > 0 && <IconBtn testId={`sponsor-up-${s.id}`} label="Move up"
                             onClick={() => { const o = b.sponsors.map((x) => x.id); [o[i - 1], o[i]] = [o[i], o[i - 1]]; run(() => tournaments.reorderSponsors(t.id, o)); }}><ArrowUp size={15} /></IconBtn>}
          <IconBtn testId={`sponsor-delete-${s.id}`} label="Remove sponsor" onClick={() => run(() => tournaments.deleteSponsor(t.id, s.id), "Sponsor removed.")}><Trash2 size={15} /></IconBtn>
        </ChalkCard>
      ))}
      {b.sponsors.length < 8 && <AddSponsor onAdd={(body) => run(() => tournaments.addSponsor(t.id, body), "Sponsor added.")} />}
    </div>
  );
}

function AddSponsor({ onAdd }) {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState(null);
  const ref = useRef();
  async function submit() {
    if (!name.trim() || !file) return toast.error("Add a sponsor name and logo.");
    await onAdd({ name: name.trim(), url, file });
    setName(""); setUrl(""); setFile(null); if (ref.current) ref.current.value = "";
  }
  return (
    <ChalkCard data-testid="sponsor-add-form" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Sponsor name" data-testid="sponsor-name" style={inputStyle} maxLength={60} />
      <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Website (optional)" data-testid="sponsor-url" style={inputStyle} />
      <input ref={ref} type="file" accept={ACCEPT} data-testid="sponsor-file" onChange={(e) => setFile(e.target.files?.[0] || null)} style={{ fontSize: 12 }} />
      <ChalkActionButton label="Add sponsor" testId="sponsor-add" onClick={submit} />
    </ChalkCard>
  );
}

function FilePick({ label, onFile, testId }) {
  const ref = useRef();
  return (
    <>
      <input ref={ref} type="file" accept={ACCEPT} hidden data-testid={`${testId}-input`}
             onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }} />
      <button data-testid={testId} onClick={() => ref.current?.click()}
              style={{ display: "inline-flex", gap: 4, alignItems: "center", padding: "7px 12px", borderRadius: 8, cursor: "pointer",
                       border: "0.5px solid var(--chalk-border)", background: "transparent", color: "var(--green-mid)", fontWeight: 700, fontSize: 12 }}>
        <ImagePlus size={14} aria-hidden="true" /> {label}
      </button>
    </>
  );
}

function LogoBox({ url, small, testId }) {
  const s = small ? 44 : 64;
  return (
    <div data-testid={testId} style={{ width: s, height: s, borderRadius: 10, background: "#FBF8F2", border: "0.5px solid var(--chalk-border)",
                                       display: "grid", placeItems: "center", overflow: "hidden", flexShrink: 0 }}>
      {url ? <img src={url} alt="" style={{ maxWidth: "88%", maxHeight: "88%", objectFit: "contain" }} /> : <ImagePlus size={18} color="#B9B4A8" />}
    </div>
  );
}

function IconBtn({ children, onClick, label, testId }) {
  return (
    <button aria-label={label} data-testid={testId} onClick={onClick}
            style={{ background: "transparent", border: "none", color: "var(--chalk-muted)", cursor: "pointer", padding: 6 }}>{children}</button>
  );
}
